import React, { useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import * as S from './AdvancesStyles';

const fmt = n => (n < 0 ? '−₹' : '₹') + Math.round(Math.abs(n)).toLocaleString('en-IN');
const fmtDate = d => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const ApprovalsTab = ({
  txns,
  member,
  locLabel,
  isAdmin = true,
  onDecide,
  onViewDetails,
  onBulkApprove,
  onOpenPassbook
}) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const openPreview = url => {
    setPreviewError(false);
    setPreviewUrl(url || null);
  };
  const pendingTxns = txns
    .filter(t => t.type === 'expense' && t.approvalStatus === 'pending')
    .sort((a, b) => b.date.localeCompare(a.date));

  const columns = [
    {
      dataField: 'date',
      text: 'Date',
      sort: true,
      formatter: (cell) => fmtDate(cell)
    },
    {
      dataField: 'memberId',
      text: 'Member',
      formatter: (cell, t) => {
        const m = member(t.memberId);
        return (
          <>
            <span className="member-link" onClick={() => onOpenPassbook(m?.id || t.memberId)}>
              {m?.name || t.memberName || 'Staff'}
            </span>
            <br />
            <span
              className={`bal-tag ${m?.mode === 'advance' ? 'tag-advance' : 'tag-reimb'}`}
              style={{
                fontSize: '10.5px',
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: '6px',
                background: m?.mode === 'advance' ? '#EAF1FE' : '#F1EAFB',
                color: m?.mode === 'advance' ? '#2F6FED' : '#8B5CE0'
              }}
            >
              {m?.mode}
            </span>
          </>
        );
      }
    },
    {
      dataField: 'employee_id_col',
      text: 'Employee ID',
      formatter: (cell, t) => {
        const m = member(t.memberId);
        const empId = t.user_employee_id || t.employee_id || t.paid_by_employee_id || t.created_by_employee_id || m?.employee_id;
        if (!empId) return <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>;
        return (
          <span style={{
            fontWeight: 700,
            fontSize: '11px',
            fontFamily: 'monospace',
            color: '#2563eb',
            background: '#eff6ff',
            padding: '3px 8px',
            borderRadius: '4px',
            border: '1px solid #bfdbfe'
          }}>
            {empId}
          </span>
        );
      }
    },
    {
      dataField: 'reportingToName',
      text: 'Reporting To',
      formatter: (cell, t) =>
        t.reportingToName ? (
          <span
            style={{
              background: '#f8fafc',
              color: '#334155',
              padding: '3px 8px',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '12px',
              border: '1px solid #e2e8f0',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <i className="fa fa-user-circle-o" style={{ color: '#64748b' }}></i>
            {t.reportingToName}
          </span>
        ) : (
          <span className="rowmuted">—</span>
        )
    },
    {
      dataField: 'amount',
      text: 'Amount',
      sort: true,
      formatter: (cell) => <strong>{fmt(cell)}</strong>
    },
    {
      dataField: 'category',
      text: 'Category',
      formatter: (cell) => <S.Chip>{cell}</S.Chip>
    },
    {
      dataField: 'locId',
      text: 'Tagged To',
      formatter: (cell, t) => <S.Chip blue>{locLabel(t)}</S.Chip>
    },
    {
      dataField: 'receipt',
      text: 'Receipt',
      formatter: (cell, t) =>
        t.receipt ? (
          <span
            onClick={() => openPreview(t.proof_urls && t.proof_urls[0])}
            style={{ fontWeight: 600, color: '#2F6FED', cursor: 'pointer', textDecoration: 'underline' }}
            title="Click to preview receipt"
          >
            📎 {t.proof_urls?.length || 1} file(s)
          </span>
        ) : (
          <span className="rowmuted">None</span>
        )
    },
    {
      dataField: 'comment',
      text: 'Note',
      formatter: (cell) => <span className="rowmuted">{cell || '—'}</span>
    },
    {
      dataField: 'decision',
      text: isAdmin ? 'Decision' : 'Actions',
      isDummyField: true,
      formatter: (cell, t) => (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {onViewDetails && (
            <S.ActionIconBtn
              onClick={() => onViewDetails(t)}
              title="View Details & Proofs"
              style={{
                border: '1px solid #bae6fd',
                background: '#f0f9ff',
                color: '#0284c7'
              }}
            >
              <i className="fa fa-eye"></i>
            </S.ActionIconBtn>
          )}
          {isAdmin ? (
            <>
              <S.ActionIconBtn
                variant="approve"
                onClick={() => onDecide(t.id, 'approved')}
                title="Approve"
              >
                ✓
              </S.ActionIconBtn>
              <S.ActionIconBtn
                variant="reject"
                onClick={() => onDecide(t.id, 'rejected')}
                title="Reject"
              >
                ✕
              </S.ActionIconBtn>
            </>
          ) : (
            <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic', marginLeft: '4px' }}>
              Pending review
            </span>
          )}
        </div>
      )
    }
  ];

  return (
    <div>
      <S.CardBox style={{ marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong style={{ fontSize: '16px' }}>{pendingTxns.length}</strong> transactions waiting on approval
        </div>
        {isAdmin && (
          <S.Btn ghost sm onClick={onBulkApprove} disabled={!pendingTxns.length}>
            Approve all filtered
          </S.Btn>
        )}
      </S.CardBox>

      <S.TableWrap>
        <BootstrapTable
          keyField="id"
          data={pendingTxns}
          columns={columns}
          bordered={false}
          hover
          noDataIndication={() => (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              Nothing pending — you're all caught up.
            </div>
          )}
          pagination={paginationFactory({
            sizePerPage: 500,
            sizePerPageList: [
              { text: '10', value: 10 },
              { text: '50', value: 50 },
              { text: '100', value: 100 },
              { text: '200', value: 200 },
              { text: '500', value: 500 },
              { text: '1000', value: 1000 },
              { text: '2000', value: 2000 }
            ],
            showTotal: true
          })}
        />
      </S.TableWrap>

      {previewUrl && (
        <div
          onClick={() => setPreviewUrl(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 17, 21, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            cursor: 'zoom-out',
            padding: '30px'
          }}
        >
          <span
            onClick={() => setPreviewUrl(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '30px',
              color: '#fff',
              fontSize: '28px',
              fontWeight: 700,
              cursor: 'pointer',
              lineHeight: 1
            }}
            title="Close"
          >
            ×
          </span>
          <div
            onClick={e => e.stopPropagation()}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', maxWidth: '90vw' }}
          >
            {previewError ? (
              <div style={{ background: '#fff', borderRadius: '10px', padding: '24px 30px', textAlign: 'center', maxWidth: '480px' }}>
                <div style={{ fontSize: '13px', color: '#181A20', fontWeight: 700, marginBottom: '8px' }}>
                  Couldn't load this file as an image.
                </div>
                <div style={{ fontSize: '11px', color: '#6B7280', wordBreak: 'break-all', marginBottom: '12px' }}>
                  {previewUrl}
                </div>
                <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2F6FED', fontWeight: 600, fontSize: '13px' }}>
                  Open the file directly →
                </a>
              </div>
            ) : (
              <img
                src={previewUrl}
                alt="Receipt preview"
                onError={() => setPreviewError(true)}
                style={{
                  maxWidth: '90vw',
                  maxHeight: '85vh',
                  borderRadius: '10px',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.5)'
                }}
              />
            )}
          </div>
          {!previewError && (
            <div
              onClick={e => e.stopPropagation()}
              style={{ position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)', fontSize: '11px', color: 'rgba(255,255,255,0.6)', maxWidth: '80vw', wordBreak: 'break-all', textAlign: 'center' }}
            >
              {previewUrl}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApprovalsTab;
